import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components';
import { authService } from '../services';
import api from '../services/api';
import '../styles/DashboardPage.scss';
import '../styles/Home.scss';
import { useRole } from '../utils/RoleContext';
import { useEntityConfig } from '../utils/EntityConfigContext';
import { LEVEL_COLOURS, aggregateScore, nextLevel } from '../utils/score';
import {
  IconGlobe, IconUsers, IconGraduation, IconChartLine, IconUpload, IconClipboard, IconFileText, IconBarChart,
} from '../components/dashboardIcons';
import ElementIcon from '../components/ElementIcon';
import LevelLadder from '../components/LevelLadder';
import OutstandingNote from '../components/OutstandingNote';
import { themeVars } from '../config/theme';
import { buildOutstanding } from '../utils/outstanding';
import { useDocumentProgress } from '../utils/useDocumentProgress';

const YES_BLOCKED_REASONS = {
  prior_level_not_maintained: 'Y.E.S: prior B-BBEE level not maintained',
  no_target: 'Y.E.S: target could not be calculated',
  below_lowest_tier: 'Y.E.S: target/absorption not yet met',
};

// ─── Element & action definitions ─────────────────────────────────────────────

const ELEMENTS = [
  {
    label: 'Overview',
    tabLabel: 'Overview',
    theme: 'overview',
    desc: 'Combined B-BBEE compliance across all five scorecard elements',
    roles: ['Consultant', 'BEE Manager', 'Finance', 'HR', 'Logistics'],
    overall: true,
  },
  {
    label: 'Finance & Statutory',
    tabLabel: 'Finance & Statutory',
    theme: 'finance',
    tabPath: '/finance-statutory',
    desc: 'Financial compliance and statutory requirements',
    roles: ['Consultant', 'BEE Manager', 'Finance'],
    actions: [
      { label: 'Company Information',   desc: 'Manage company details',               Icon: IconClipboard,  path: '/finance-statutory/company-information' },
      { label: 'Manage TMPS',           desc: 'Total Measured Procurement Spend',     Icon: IconBarChart,   path: '/finance-statutory/manage-tmps' },
      { label: 'NPAT',                  desc: 'ESD and SED Target Related',           Icon: IconBarChart,   path: '/finance-statutory/npat' },
      { label: 'Finance Overview',      desc: 'Finance & Statutory summary',          Icon: IconFileText,   path: '/finance-statutory' },
    ],
    primaryAction: { label: 'Open Finance & Statutory', path: '/finance-statutory' },
  },
  {
    label: 'Ownership',
    tabLabel: 'Ownership',
    tabPath: '/ownership/',
    desc: 'Black ownership and economic interest',
    elementKeys: ['ownership'],
    roles: ['Consultant', 'BEE Manager'],
    actions: [
      { label: 'Data Sheet',          desc: 'Template & completed sheet upload',  Icon: IconUpload,     path: '/ownership/data-sheet' },
      { label: 'Ownership Scorecard', desc: 'Points & calculation',               Icon: IconClipboard,  path: '/ownership/scorecard' },
      { label: 'Documentation',       desc: 'Verification evidence upload',       Icon: IconFileText,   path: '/ownership/documentation' },
      { label: 'KPI & Reports',       desc: 'Charts and trend reports',           Icon: IconChartLine,  path: '/ownership/reports' },
    ],
    primaryAction: { label: 'Open Ownership Scorecard', path: '/ownership/' },
    dataSheetPath: '/ownership/data-sheet',
  },
  {
    label: 'Management Control',
    tabLabel: 'Management Control',
    tabPath: '/management/management-control',
    desc: 'Board and management composition data, YES4YOUTH',
    roles: ['Consultant', 'BEE Manager', 'HR'],
    elementKeys: ['management_control'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Management Control data',      Icon: IconUpload,     path: '/management/submit-datasheet' },
      { label: 'MC Scorecard',      desc: 'Points & calculation',                Icon: IconClipboard,  path: '/management/scorecard' },
      { label: 'Documentation',     desc: 'Verification evidence upload',        Icon: IconFileText,   path: '/management/documentation/management_control' },
      { label: 'Reports',           desc: 'Charts and trend reports',            Icon: IconChartLine,  path: '/reports/scorecard' },
      { label: 'YES4YOUTH',         desc: 'YES4YOUTH Documentation Management',  Icon: IconFileText,   path: '/management/yes4youth'},
    ],
    primaryAction: { label: 'Open MC Scorecard', path: '/management/management-control' },
    dataSheetPath: '/management/submit-datasheet',
  },
  {
    label: 'Management Control (Transport)',
    tabLabel: 'Management Control',
    tabPath: '/management/management-control/management-control-transport',
    desc: 'Board and management composition data, YES4YOUTH',
    roles: ['Consultant', 'BEE Manager', 'HR'],
    elementKeys: ['management_control_transport'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Management Control data',      Icon: IconUpload,     path: '/management/submit-datasheet/management-control-transport' },
      { label: 'MC Scorecard',      desc: 'Points & calculation',                Icon: IconClipboard,  path: '/management/scorecard/management-control-transport' },
      { label: 'Documentation',     desc: 'Verification evidence upload',        Icon: IconFileText,   path: '/management/documentation/management_control_transport' },
      { label: 'Reports',           desc: 'Charts and trend reports',            Icon: IconChartLine,  path: '/reports/scorecard' },
      { label: 'YES4YOUTH',         desc: 'YES4YOUTH Documentation Management',  Icon: IconFileText,   path: '/management/yes4youth'},
    ],
    primaryAction: { label: 'Open MC Scorecard', path: '/management/scorecard/management-control-transport' },
    dataSheetPath: '/management/submit-datasheet/management-control-transport',
  },
  {
    label: 'Employment Equity',
    tabLabel: 'Employment Equity',
    tabPath: '/management/employment-equity',
    desc: 'Employee composition data across senior, middle and junior management',
    roles: ['Consultant', 'BEE Manager', 'HR'],
    elementKeys: ['employment_equity'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Employment Equity data',       Icon: IconUpload,     path: '/management/submit-datasheet/employment-equity' },
      { label: 'EE Scorecard',      desc: 'Points & calculation',                Icon: IconClipboard,  path: '/management/scorecard/employment-equity' },
      { label: 'Documentation',     desc: 'Verification evidence upload',        Icon: IconFileText,   path: '/management/documentation/employment_equity' },
      { label: 'Reports',           desc: 'Charts and trend reports',            Icon: IconChartLine,  path: '/reports/scorecard' },
    ],
    primaryAction: { label: 'Open EE Scorecard', path: '/management/scorecard/employment-equity' },
    dataSheetPath: '/management/submit-datasheet/employment-equity',
  },
  {
    label: 'Skills Development',
    tabLabel: 'Skills Development',
    tabPath: '/skills-development',
    desc: 'Training and development initiatives',
    elementKeys: ['skills_development'],
    roles: ['Consultant', 'BEE Manager', 'HR'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Skills Development data',     Icon: IconUpload,     path: '/skills-development/submit-datasheet' },
      { label: 'Skills Scorecard',  desc: 'Points & calculation',               Icon: IconClipboard,  path: '/skills-development/scorecard' },
      { label: 'Documentation',     desc: 'Verification evidence upload',       Icon: IconFileText,   path: '/skills-development/documentation/skills_development' },
      { label: 'Reports',           desc: 'Charts and trend reports',           Icon: IconChartLine,  path: '/reports/skills-development' },
    ],
    primaryAction: { label: 'Open Skills Scorecard', path: '/skills-development/scorecard' },
    dataSheetPath: '/skills/submit-datasheet',
  },

   //----------------- Transport Road Freight Generic-----------------------
  {
    label: 'Skills Development (Transport)',
    tabLabel: 'Skills Development',
    tabPath: '/skills-development/skills-development-transport',
    desc: 'Training and development initiatives',
    elementKeys: ['skills_development_transport'],
    roles: ['Consultant', 'BEE Manager', 'HR'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Skills Development data',     Icon: IconUpload,     path: '/skills-development/submit-datasheet/skills-development-transport' },
      { label: 'Skills Scorecard',  desc: 'Points & calculation',               Icon: IconClipboard,  path: '/skills-development/scorecard/skills-development-transport' },
      { label: 'Documentation',     desc: 'Verification evidence upload',       Icon: IconFileText,   path: '/skills-development/documentation/skills_development_transport' },
      { label: 'Reports',           desc: 'Charts and trend reports',           Icon: IconChartLine,  path: '/reports/skills-development' },
    ],
    primaryAction: { label: 'Open Skills Scorecard', path: '/skills-development/scorecard/skills-development-transport' },
    dataSheetPath: '/skills/submit-datasheet/skills-development-transport',
  },

     //----------------- COGP Specialised Generic-----------------------
  {
    label: 'Skills Development Specialised',
    tabLabel: 'Skills Development',
    tabPath: '/skills-development/skills-development-specialised',
    desc: 'Training and development initiatives',
    elementKeys: ['skills_development_specialised'],
    roles: ['Consultant', 'BEE Manager', 'HR'],
    actions: [
      { label: 'Submit Data Sheet', desc: 'Upload Skills Development data',     Icon: IconUpload,     path: '/skills-development/submit-datasheet/skills-development-specialised' },
      { label: 'Skills Scorecard',  desc: 'Points & calculation',               Icon: IconClipboard,  path: '/skills-development/scorecard/skills-development-specialised' },
      { label: 'Documentation',     desc: 'Verification evidence upload',       Icon: IconFileText,   path: '/skills-development/documentation' },
      { label: 'Reports',           desc: 'Charts and trend reports',           Icon: IconChartLine,  path: '/reports/skills-development' },
    ],
    primaryAction: { label: 'Open Skills Scorecard', path: '/skills-development/scorecard/skills-development-specialised' },
    dataSheetPath: '/skills/submit-datasheet/skills-development-specialised',
  },
  {
    label: 'Preferential Procurement',
    tabLabel: 'Preferential Procurement',
    tabPath: '/esd/preferential-procurement',
    desc: 'Preferential Procurement',
    roles: ['Consultant', 'BEE Manager', 'Logistics'],
    elementKeys: ['procurement'],
    actions: [
      { label: 'Preferential Procurement',          desc: 'Procurement scorecard & data sheet',     Icon: IconClipboard,  path: '/esd/preferential-procurement' },
      { label: 'Reports',                           desc: 'PP charts and trend reports',            Icon: IconChartLine,  path: '/reports/preferential-procurement' },
    ],
    primaryAction: { label: 'Open Preferential Procurement', path: '/esd/preferential-procurement' },
  },
  {
    label: 'Enterprise & Supplier Development',
    tabLabel: 'Enterprise & Supplier Development',
    tabPath: '/esd/enterprise-supplier-development',
    desc: 'Enterprise Development, Supplier Development',
    roles: ['Consultant', 'BEE Manager', 'Logistics'],
    elementKeys: ['enterprise_development', 'supplier_development'],
    actions: [
      { label: 'Enterprise & Supplier Development', desc: 'Enterprise & Supplier Development management',             Icon: IconBarChart,   path: '/esd/enterprise-supplier-development' },
      { label: 'Reports',                           desc: 'Charts and trend reports',                                 Icon: IconChartLine,  path: '/reports/preferential-procurement' },
    ],
    primaryAction: { label: 'Open Enterprise & Supplier Development', path: '/esd/enterprise-supplier-development' },
  },

  //----------------- Transport Road Freight Generic-----------------------
  {
    label: 'Enterprise Development',
    tabLabel: 'Enterprise Development',
    tabPath: '/esd/enterprise-development',
    desc: 'Enterprise Development',
    roles: ['Consultant', 'BEE Manager', 'Logistics'],
    elementKeys: ['enterprise_development_transport'],
    actions: [
      { label: 'Enterprise Development',            desc: 'Enterprise Development Management',            Icon: IconBarChart,   path: '/esd/enterprise-supplier-development/enterprise-development-transport' },
      { label: 'Reports',                           desc: 'Charts and trend reports',                  Icon: IconChartLine,  path: '/reports/enterprise-development' },
    ],
    primaryAction: { label: 'Open Enterprise Development', path: '/esd/enterprise-supplier-development/scorecard/enterprise-development-transport' },
  },
  {
    label: 'Socio-Economic Development',
    tabLabel: 'Socio-Economic Development',
    tabPath: '/socio-economic-development',
    desc: 'Community investment and social spend',
    elementKeys: ['sed'],
    roles: ['Consultant', 'BEE Manager'],
    maxPoints: 5,
    actions: [
      { label: 'SED Scorecard',   desc: 'Points & calculation',               Icon: IconClipboard,   path: '/sed/scorecard' },
      { label: 'Submit Data',     desc: 'Upload SED data sheet',              Icon: IconUpload,      path: '/sed/submit-datasheet' },
      { label: 'Documentation',   desc: 'Verification evidence upload',       Icon: IconFileText,    path: '/socio-economic-development/documentation/sed' },
      { label: 'Reports',         desc: 'Charts and trend reports',           Icon: IconChartLine,   path: '/reports/socio-economic-development' },
    ],
    primaryAction: { label: 'Open SED', path: '/socio-economic-development' },
  },
  
  //----------------- Transport Road Freight Generic-----------------------
  {
    label: 'Socio-Economic Development (Transport)',
    tabLabel: 'Socio-Economic Development',
    tabPath: '/socio-economic-development/sed-transport',
    desc: 'Community investment and social spend',
    elementKeys: ['sed_transport'],
    roles: ['Consultant', 'BEE Manager'],
    maxPoints: 5,
    actions: [
      { label: 'SED Scorecard',   desc: 'Points & calculation',               Icon: IconClipboard,   path: '/sed/scorecard/sed-transport' },
      { label: 'Submit Data',     desc: 'Upload SED data sheet',              Icon: IconUpload,      path: '/sed/submit-datasheet/sed-transport' },
      { label: 'Documentation',   desc: 'Verification evidence upload',       Icon: IconFileText,    path: '/socio-economic-development/documentation/sed_transport' },
      { label: 'Reports',         desc: 'Charts and trend reports',           Icon: IconChartLine,   path: '/reports/socio-economic-development' },
    ],
    primaryAction: { label: 'Open SED', path: '/sed/scorecard/sed-transport' },
  },
  {
    label: 'Reports',
    tabLabel: 'Reports',
    theme: 'reports',
    tabPath: '/reports',
    desc: 'Download detailed compliance reports for all B-BBEE elements',
    roles: ['Consultant', 'BEE Manager', 'HR', 'Logistics'],
    actions: [
      { label: 'All Reports',                     desc: 'Full B-BBEE Scorecard Report',           Icon: IconFileText,   path: '/reports/all' },
      { label: 'Ownership Report',                desc: 'Ownership KPI trends',                   Icon: IconGlobe,      path: '/reports/ownership' },
      { label: 'Management Control Report',       desc: 'Management Control KPIs',                Icon: IconUsers,      path: '/reports/scorecard' },
      { label: 'Skills Report',                   desc: 'Skills Development KPIs',                Icon: IconGraduation, path: '/reports/skills-development' },
      { label: 'PP Report',                       desc: 'Preferential Procurement Report',        Icon: IconBarChart,   path: '/reports/preferential-procurement' },
      { label: 'Enterprise & Supplier Report',   desc: 'Enterprise & Supplier Development Report',Icon: IconBarChart,   path: '/reports/enterprise-development' },
      { label: 'SED Report',                      desc: 'Socio-Economic Development Report',      Icon: IconBarChart,   path: '/reports/socio-economic-development' },
    ],
    primaryAction: { label: 'View All Reports', path: '/reports/all' },
  },
];

// Theme comes from the backend element key, so there is nothing extra to keep in sync.
const elementTheme = (e) => e.theme || e.elementKeys?.[0] || 'overview';

const SCORE_FETCH_TARGETS = Object.values(
  ELEMENTS.reduce((acc, e) => {
    (e.elementKeys || []).forEach((key) => {
      if (!acc[key]) {
        acc[key] = { key, roles: e.roles, url: `/core/scorecard/get_scorecard/?element=${key}` };
      }
    });
    return acc;
  }, {})
);

const OVERALL_ACTIONS = [
  { label: 'All Reports',             desc: 'View B-BBEE reports across all elements',    Icon: IconChartLine,  path: '/reports' },
  { label: 'All Data Sheets',         desc: 'Access and upload all element data sheets',  Icon: IconClipboard,  path: '/all-data-sheets' },
  { label: 'Download All Documents',  desc: 'Download the full combined B-BBEE report',   Icon: IconFileText,   path: '/reports/all' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Home = () => {
  const navigate = useNavigate();
  const { role, setRole } = useRole();
  const { bee_sector, entity_size } = useEntityConfig();
  const [availableElements, setAvailableElements] = useState(null); //where null means 'not loaded'
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [scores, setScores] = useState({});
  const [scoresLoaded, setScoresLoaded] = useState(false);
  const [daysToVerification, setDaysToVerification] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [beeLevel, setBEELevel] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  //Determines which Elements displyed depending on the Sector 
  useEffect (() => {
    api.get('/core/scorecard/list_elements/')
    .then(res => {
      const keys = new Set(res.data.flatMap(group => group.elements));
      setAvailableElements(keys);
    })
    .catch(() => {});
  }, [bee_sector, entity_size]);

  const visibleElements = ELEMENTS.filter(e => 
  (e.overall || !role || e.roles.includes(role)) &&
  (!e.elementKeys || availableElements === null || e.elementKeys.every(k => availableElements.has(k)))
  );

  const docProgress = useDocumentProgress(
    visibleElements.filter((e) => e.elementKeys).map((e) => ({ key: e.elementKeys[0], elementId: e.elementKeys[0] })),
  );

  useEffect(() => {
    authService.getCurrentUser()
      .then((userData) => { setUser(userData); setRole(userData.role ?? null); setIsLoading(false); })
      .catch(() => navigate('/login'));
  }, [navigate]);

  useEffect(() => {
    const targets = SCORE_FETCH_TARGETS.filter((t) => !role || t.roles.includes(role));
    Promise.allSettled(targets.map(({ key, url }) =>
      api.get(url).then((res) => {
        if (res.data?.total_points != null) {
          const inds = res.data.indicators || [];
          const maxPoints = inds.reduce((s, ind) => s + (ind.points || 0), 0);
          const totalSubMin = res.data.results?.find((r) => r.id === '_total_sub_min');
          const subMinimumMet = res.data.sub_minimum_met ?? totalSubMin?.sub_minimum_met ?? null;
          // Claimed ED/SD bonus adds 1 point, matching the backend combined total and ESDScorecard
          const points = res.data.total_points + (res.data.bonus_selected ? 1 : 0);
          setScores((prev) => ({ ...prev, [key]: { points, maxPoints, subMinimumMet } }));
        }
      }))).then(() => setScoresLoaded(true));
  }, [role]);

  useEffect(() => {
    api.get('/core/company-info/')
      .then((res) => {
        if (res.data?.days_to_verification != null) {
          setDaysToVerification(res.data.days_to_verification);
        }
        if (res.data?.company_name) {
          setCompanyName(res.data.company_name);
        }
      })
      .catch(() => {});

    api.get('/core/scorecard/combined-total/')
      .then((res) => { setBEELevel(res.data); })
      .catch(() => {});

    api.get('/core/financial-years/current/')
      .then((res) => { if (res.data?.label) setFinancialYear(res.data); })
      .catch(() => {});
  }, []);


  if (isLoading) {
    return (
      <div className="home-page">
        <div className="home-loading">Loading...</div>
      </div>
    );
  }

  const el = visibleElements[activeTab] ?? visibleElements[0];
  const actions = el.overall ? OVERALL_ACTIONS : el.actions;
  const nonOverallElements = visibleElements.filter(
    (e) => !e.overall && e.label !== 'Reports' && e.label !== 'Finance & Statutory'
  );

  // Sticky-note items, built only from what the backend returned.
  const outstanding = scoresLoaded
    ? buildOutstanding(
        nonOverallElements.filter((e) => e.elementKeys).map((e) => ({
          key: e.elementKeys[0], label: e.label, score: aggregateScore(scores, e.elementKeys),
        })),
        docProgress,
      )
    : [];

  const openOutstanding = (item) => {
    const elem = nonOverallElements.find((e) => e.elementKeys?.[0] === item.key);
    if (!elem) return;
    const docsPath = elem.actions?.find((a) => a.label === 'Documentation')?.path;
    const path = item.kind === 'docs' ? docsPath : item.kind === 'data' ? elem.dataSheetPath : null;
    if (path) navigate(path);
    else setActiveTab(visibleElements.indexOf(elem));
  };

  const next = beeLevel ? nextLevel(beeLevel.combined_points, beeLevel.thresholds) : null;

  const tabPoints = (elem) => {
    if (elem.overall) return beeLevel ? `${Number(beeLevel.combined_points).toFixed(1)} pts` : '';
    if (!elem.elementKeys) return '';
    const s = aggregateScore(scores, elem.elementKeys);
    return s ? `${s.points.toFixed(1)} of ${s.maxPoints}` : 'No data yet';
  };

  return (
    <div className="folder-stage">
      <div className="folder">

        {/* ── Tab strip ── */}
        <div className="folder-tabs" role="tablist">
          {visibleElements.map((elem, i) => (
            <button
              key={elem.label}
              role="tab"
              aria-selected={i === activeTab}
              className={`folder-tab themed${i === activeTab ? ' active' : ''}`}
              style={themeVars(elementTheme(elem))}
              onClick={() => setActiveTab(i)}
            >
              <ElementIcon element={elementTheme(elem)} />
              <span className="folder-tab-label">{elem.tabLabel}</span>
              <span className="folder-tab-pts">{tabPoints(elem)}</span>
            </button>
          ))}
        </div>

        {/* ── Folder page ── */}
        <div className="folder-page themed" style={themeVars(elementTheme(el))}>
          <div className="folder-content">

            {/* Page header */}
            <div className="folder-page-head">
              <div className="left">
                <span className="folder-page-eyebrow">
                  {[companyName, financialYear?.label].filter(Boolean).join(' · ') || 'B-BBEE dashboard'}
                </span>
                <h2 className="folder-page-title">{el.label}</h2>
                <p className="folder-page-sub">{el.desc}</p>
              </div>
              {el.overall && (
                <div className="folder-page-pts">
                  {user && <span className="welcome">Welcome back, {user.first_name || user.username}</span>}
                  {beeLevel && (
                    <>
                      <span className="level-badge" style={{ backgroundColor: LEVEL_COLOURS[beeLevel.level] }}>
                        {beeLevel.level}
                      </span>
                      <span className="big">
                        {Number(beeLevel.combined_points).toFixed(2)} <small>/ {beeLevel.max_points}</small>
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Overview: level ladder + outstanding note. Element tabs: three stats. */}
            {el.overall ? (
              <div className="folder-overview">
                <section>
                  <h3 className="folder-section-title">Your level</h3>
                  <LevelLadder beeLevel={beeLevel} />
                  <p className="folder-overview-note">
                    {beeLevel ? (
                      <>
                        <b>{Number(beeLevel.combined_points).toFixed(2)} of {beeLevel.max_points} points.</b>
                        {next && <> You need <b>{next.needed.toFixed(2)} more for {next.label}</b>.</>}
                        {beeLevel.levels_discounted > 0 &&
                          ` ${beeLevel.levels_discounted} level${beeLevel.levels_discounted > 1 ? 's' : ''} discounted because a sub-minimum is not met.`}
                      </>
                    ) : 'Upload data sheets to calculate your level.'}
                  </p>
                  {beeLevel?.yes?.qualifies_for_recognition && (
                    <p className="folder-stat-trend folder-stat-trend--yes">
                      {`Y.E.S: +${beeLevel.yes.levels_gained} level${beeLevel.yes.levels_gained !== 1 ? 's' : ''}`}
                      {beeLevel.yes.bonus_points_gained > 0 ? ` +${beeLevel.yes.bonus_points_gained} bonus points` : ''}
                    </p>
                  )}
                  {beeLevel?.yes && !beeLevel.yes.qualifies_for_recognition && YES_BLOCKED_REASONS[beeLevel.yes.reason] && (
                    <p className="folder-stat-trend folder-stat-trend--yes-blocked">
                      {YES_BLOCKED_REASONS[beeLevel.yes.reason]}
                    </p>
                  )}
                  {daysToVerification == null && (
                    <p className="folder-stat-trend">
                      Set the B-BBEE certificate expiry in Company Information to see verification dates.
                    </p>
                  )}
                </section>
                <OutstandingNote items={outstanding} daysToVerification={daysToVerification} onSelect={openOutstanding} collapsible />
              </div>
            ) : (
            <div className="folder-stats">
              {el.label !== 'Finance & Statutory' && el.label !== 'Reports' && (() => {
                const s = aggregateScore(scores, el.elementKeys);
                const compliancePct = s ? Math.round((s.points / s.maxPoints) * 100) : null;
                return (
                  <>
                    <div className="folder-stat">
                      <div className="folder-stat-label">Points Achieved</div>
                      <div className="folder-stat-value">
                        {s ? s.points.toFixed(2) : '—'}
                        <span className="unit">/ {s ? s.maxPoints : '—'}</span>
                      </div>
                      <div className="folder-stat-trend">{s ? 'From last scorecard calculation' : 'Upload data sheet to calculate'}</div>
                    </div>
                    <div className="folder-stat">
                      <div className="folder-stat-label">Compliance</div>
                      <div className="folder-stat-value">
                        {compliancePct != null ? compliancePct : '—'}
                        <span className="unit">%</span>
                      </div>
                      <div className="folder-stat-trend">
                        {compliancePct != null
                          ? s.subMinimumMet === false ? 'Sub-minimum not met — action needed' : 'On track'
                          : 'Pending scorecard calculation'}
                      </div>
                    </div>
                    <div className="folder-stat">
                      <div className="folder-stat-label">Sub-minimum</div>
                      <div className={`folder-stat-value${s && s.subMinimumMet === false ? ' is-bad' : ''}`}>
                        {s ? (s.subMinimumMet ? 'Met' : 'Not Met') : '—'}
                      </div>
                      <div className="folder-stat-trend">Indicator-level threshold</div>
                    </div>
                  </>
                );
              })()}
            </div>
            )}

            {/* Overview: element progress list */}
            {el.overall && (
              <div className="folder-elements">
                <h3 className="folder-section-title">Elements</h3>
                <div className="folder-elements-label">Element overview — select an element to manage</div>
                {nonOverallElements
                  // Preferential Procurement is shown as a sub-row of ESD; only list it on its
                  // own when there is no ESD group (e.g. Road Freight's ED-only element)
                  .filter((elem) => !(elem.label === 'Preferential Procurement' &&
                    nonOverallElements.some((e) => e.label === 'Enterprise & Supplier Development')))
                  .map((elem, i) => {
                  const s = aggregateScore(scores, elem.elementKeys);
                  const pct = s ? Math.min((s.points / s.maxPoints) * 100, 100) : 0;
                  const statusCls = !s ? 'status-empty' : s.subMinimumMet === false ? 'status-warn' : 'status-ok';
                  const statusLabel = !s ? 'No data' : s.subMinimumMet === false ? 'Action needed' : 'On track';

                  if (elem.label === 'Enterprise & Supplier Development') {
                    const ppScore = scores['procurement'];
                    const edScore = scores['enterprise_development'];
                    const sdScore = scores['supplier_development'];
                    const esdTotal = (ppScore?.points ?? 0) + (edScore?.points ?? 0) + (sdScore?.points ?? 0);
                    const esdMaxPoints = (ppScore?.maxPoints ?? 0) + (edScore?.maxPoints ?? 0) + (sdScore?.maxPoints ?? 0);
                    const esdHasData = ppScore || edScore || sdScore;
                    const esdPct = esdMaxPoints > 0 ? Math.min((esdTotal / esdMaxPoints) * 100, 100) : 0;
                    const esdSubMinMet = ppScore?.subMinimumMet !== false && edScore?.subMinimumMet !== false && sdScore?.subMinimumMet !== false;
                    const esdStatusCls = !esdHasData ? 'status-empty' : !esdSubMinMet ? 'status-warn' : 'status-ok';
                    const esdStatusLabel = !esdHasData ? 'No data' : !esdSubMinMet ? 'Action needed' : 'On track';
                    return (
                      <div key={elem.label} className="folder-element-group">
                        <button
                          className="folder-element-row folder-element-row--parent themed"
                          style={themeVars(elementTheme(elem))}
                          onClick={() => setActiveTab(visibleElements.indexOf(elem))}
                        >
                          <span className="folder-element-row-icon"><ElementIcon element="esd" size="sm" /></span>
                          <span className="folder-element-row-name">Enterprise & Supplier Development</span>
                          <span className="folder-element-row-bar"><i style={{ width: `${esdPct}%` }} /></span>
                          <span className="folder-element-row-pts">
                            {esdHasData ? esdTotal.toFixed(2) : '—'}
                            <span className="of"> / {esdMaxPoints || '—'}</span>
                          </span>
                          <span className={`folder-element-row-status ${esdStatusCls}`}>{esdStatusLabel}</span>
                        </button>
                        <div className="folder-element-subrows">
                          <button
                            className="folder-element-subrow themed"
                            style={themeVars('procurement')}
                            onClick={() => navigate('/esd/preferential-procurement/scorecard')}
                          >
                            <span className="folder-element-subrow-icon"><ElementIcon element="procurement" size="sm" /></span>
                            <span className="folder-element-subrow-name">Preferential Procurement</span>
                            <span className="folder-element-subrow-bar"><i style={{ width: `${ppScore?.maxPoints > 0 ? Math.min((ppScore.points / ppScore.maxPoints) * 100, 100) : 0}%` }} /></span>
                            <span className="folder-element-subrow-pts">
                              {ppScore ? ppScore.points.toFixed(2) : '—'}
                              <span className="of"> / {ppScore?.maxPoints || '—'}</span>
                            </span>
                            <span className={`folder-element-row-status ${ppScore ? (ppScore.subMinimumMet === false ? 'status-warn' : 'status-ok') : 'status-empty'}`}>
                              {ppScore ? (ppScore.subMinimumMet === false ? 'Action needed' : 'On track') : 'No data'}
                            </span>
                          </button>
                          <button
                            className="folder-element-subrow themed"
                            style={themeVars('esd')}
                            onClick={() => navigate('/esd/enterprise-supplier-development/scorecard')}
                          >
                            <span className="folder-element-subrow-icon"><ElementIcon element="esd" size="sm" /></span>
                            <span className="folder-element-subrow-name">Enterprise Development</span>
                            <span className="folder-element-subrow-bar"><i style={{ width: `${edScore?.maxPoints > 0 ? Math.min((edScore.points / edScore.maxPoints) * 100, 100) : 0}%` }} /></span>
                            <span className="folder-element-subrow-pts">
                              {edScore ? edScore.points.toFixed(2) : '—'}
                              <span className="of"> / {edScore?.maxPoints || '—'}</span>
                            </span>
                            <span className={`folder-element-row-status ${edScore ? (edScore.subMinimumMet === false ? 'status-warn' : 'status-ok') : 'status-empty'}`}>
                              {edScore ? (edScore.subMinimumMet === false ? 'Action needed' : 'On track') : 'No data'}
                            </span>
                          </button>
                          <button
                            className="folder-element-subrow themed"
                            style={themeVars('esd')}
                            onClick={() => navigate('/esd/enterprise-supplier-development/scorecard')}
                          >
                            <span className="folder-element-subrow-icon"><ElementIcon element="esd" size="sm" /></span>
                            <span className="folder-element-subrow-name">Supplier Development</span>
                            <span className="folder-element-subrow-bar"><i style={{ width: `${sdScore?.maxPoints > 0 ? Math.min((sdScore.points / sdScore.maxPoints) * 100, 100) : 0}%` }} /></span>
                            <span className="folder-element-subrow-pts">
                              {sdScore ? sdScore.points.toFixed(2) : '—'}
                              <span className="of"> / {sdScore?.maxPoints || '—'}</span>
                            </span>
                            <span className={`folder-element-row-status ${sdScore ? (sdScore.subMinimumMet === false ? 'status-warn' : 'status-ok') : 'status-empty'}`}>
                              {sdScore ? (sdScore.subMinimumMet === false ? 'Action needed' : 'On track') : 'No data'}
                            </span>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={elem.label}
                      className="folder-element-row themed"
                      style={themeVars(elementTheme(elem))}
                      onClick={() => setActiveTab(visibleElements.indexOf(elem))}
                    >
                      <span className="folder-element-row-icon"><ElementIcon element={elementTheme(elem)} size="sm" /></span>
                      <span className="folder-element-row-name">{elem.label}</span>
                      <span className="folder-element-row-bar"><i style={{ width: `${pct}%` }} /></span>
                      <span className="folder-element-row-pts">
                        {s ? s.points.toFixed(2) : '—'}
                        <span className="of"> / {s ? s.maxPoints : (elem.maxPoints ?? '—')}</span>
                      </span>
                      <span className={`folder-element-row-status ${statusCls}`}>{statusLabel}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Action cards */}
            <div className="folder-nav-grid">
              {actions.map((action) => (
                <button
                  key={action.label}
                  className="folder-nav-card"
                  onClick={() => navigate(action.path)}
                >
                  <span className="folder-nav-card-icon"><action.Icon /></span>
                  <span className="folder-nav-card-label">{action.label}</span>
                  <span className="folder-nav-card-desc">{action.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
